// useSendBroadcast + useBroadcastPreview (issue #419) — broadcast composer
// support. The preview hook returns the resolved recipient count so the
// confirmation modal in #4.5 can render "Send to N contractors" before the
// actual POST fires.
//
// Backend gap (#419): the Sprint 2 controller only exposes the *commit*
// endpoint (`POST /chats/jobs/:jobId/broadcasts`); the
// `previewRecipientCount` service method is not surfaced as a REST route.
// We implement the preview client-side by reading the assigned-job roster
// from the existing `getEventAndUserJobs`-style endpoints and applying the
// target rule locally. This may drift from the server's exact resolver if
// the server gains new target rules — flagged in PR caveats.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { sendBroadcast } from './api';
import { chatKeys } from './keys';

import type {
  BroadcastSendBody,
  BroadcastSendResult,
  BroadcastTarget,
} from 'src/models/chat';

interface SendArgs {
  jobId: number;
  body: BroadcastSendBody;
}

export function useSendBroadcast() {
  const queryClient = useQueryClient();
  return useMutation<BroadcastSendResult, unknown, SendArgs>(
    ({ jobId, body }) => sendBroadcast(jobId, body),
    {
      onSuccess: (_result, { jobId }) => {
        queryClient.invalidateQueries(chatKeys.myChatsRoot());
        queryClient.invalidateQueries(chatKeys.jobChats(jobId));
      },
    }
  );
}

/**
 * Roster row consumed by `previewRecipientCount`. The shape matches the
 * subset of `Job` fields the broadcast resolver needs: assigned userId,
 * confirmation/check-in flags. Callers pull this from whichever query
 * already has the data — the broadcast composer reads the same job-detail
 * roster the panel renders.
 */
export interface BroadcastRosterRow {
  userId: number | null | undefined;
  isConfirmed?: boolean | null;
  hasCheckedIn?: boolean | null;
}

/**
 * Resolve the recipient count for a target rule against a roster. Returns
 * `null` if we can't compute it client-side (custom rule with no selected
 * userIds, missing roster).
 */
export function previewRecipientCount(
  roster: ReadonlyArray<BroadcastRosterRow>,
  target: BroadcastTarget,
  customUserIds?: number[]
): number | null {
  if (!Array.isArray(roster)) return null;
  switch (target) {
    case 'ALL_ASSIGNED':
      return roster.filter(
        (r) => typeof r.userId === 'number' && r.userId > 0
      ).length;
    case 'CONFIRMED_ONLY':
      return roster.filter(
        (r) =>
          typeof r.userId === 'number' && r.userId > 0 && r.isConfirmed === true
      ).length;
    case 'NOT_CHECKED_IN':
      return roster.filter(
        (r) =>
          typeof r.userId === 'number' &&
          r.userId > 0 &&
          r.hasCheckedIn !== true
      ).length;
    case 'CUSTOM_USER_IDS': {
      if (!customUserIds) return 0;
      const assignedIds = new Set(
        roster
          .map((r) => r.userId)
          .filter((id): id is number => typeof id === 'number' && id > 0)
      );
      return customUserIds.filter((id) => assignedIds.has(id)).length;
    }
    default:
      return null;
  }
}
